import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { emailAuthApi, passwordIsValid } from "../api/emailAuthApi";
import { AuthLayout, AuthField, AuthError, AuthNotice, PasswordChecklist, CodeInput } from "../components/AuthLayout";
import { APP_TITLE } from "../../../branding";

// Invite link (purpose=invite): email + new password + confirm → scan QR → confirm code.
// 2FA reset link (purpose=totp_reset): email + current password → scan QR → confirm code.
export default function EmailRegisterPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [info, setInfo] = useState(null);       // { purpose, email, display_name }
  const [step, setStep] = useState("loading");  // loading | invalid | credentials | totp | done
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [provisioning, setProvisioning] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setStep("invalid"); return; }
    emailAuthApi.linkInfo(token)
      .then((d) => { setInfo(d); setStep("credentials"); })
      .catch((e) => { setError(e.message); setStep("invalid"); });
  }, [token]);

  const isReset = info?.purpose === "totp_reset";
  const canSubmitCredentials = isReset ? !!password : passwordIsValid(password) && password === confirm;

  const submitCredentials = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const p = await emailAuthApi.registerStart({
        token,
        email: info.email,
        password,
        confirm_password: isReset ? "" : confirm,
      });
      setProvisioning(p);
      setPassword(""); setConfirm("");
      setStep("totp");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await emailAuthApi.registerConfirm(token, code);
      setStep("done");
    } catch (err) {
      setCode("");
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (step === "loading") {
    return <AuthLayout eyebrow="Account setup" title="Checking your link…" />;
  }

  if (step === "invalid") {
    return (
      <AuthLayout eyebrow="Account setup" title="This link can't be used">
        <AuthError message={error || "This link is invalid or has expired."} />
        <p className="auth-fineprint">Setup links work once and expire after 72 hours. Ask your ProTrack administrator to send a new invitation.</p>
        <div className="auth-links"><Link className="auth-link" to="/">← Go to sign in</Link></div>
      </AuthLayout>
    );
  }

  if (step === "done") {
    return (
      <AuthLayout eyebrow="All set" title={isReset ? "Two-factor is set up" : "Registration complete"}>
        <AuthNotice>
          Sign in with <b>{info.email}</b>, your password and the current code from your authenticator app.
        </AuthNotice>
        <Link className="auth-btn auth-btn--primary" to="/email-signin">Go to sign in</Link>
      </AuthLayout>
    );
  }

  if (step === "totp") {
    return (
      <AuthLayout eyebrow="Step 2 of 2 · Two-factor" title="Add ProTrack to your authenticator"
        subtitle="Scan this QR code with any authenticator app (Microsoft Authenticator, Google Authenticator, Authy, 1Password…), then enter the 6-digit code it shows.">
        <form className="auth-form" onSubmit={submitCode}>
          <div className="auth-qr">
            <img src={provisioning.qr_svg_data_uri} alt={`TOTP QR code for ${APP_TITLE}`} width="200" height="200" />
            <div className="auth-qr__manual">
              <span className="auth-field__label">Can't scan? Enter this key</span>
              <code className="auth-qr__key">{provisioning.manual_key}</code>
            </div>
          </div>
          <AuthField label="6-digit code">
            <CodeInput value={code} onChange={setCode} />
          </AuthField>
          <AuthError message={error} />
          <button className="auth-btn auth-btn--primary" type="submit" disabled={busy || code.length !== 6}>
            {busy ? "Confirming…" : "Confirm and finish"}
          </button>
          <p className="auth-fineprint">Keep this QR code private. It won't be shown again once setup is complete.</p>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow={isReset ? "Step 1 of 2 · Confirm it's you" : "Step 1 of 2 · Create your sign-in"}
      title={isReset ? "Set up two-factor again" : `Welcome${info.display_name ? `, ${info.display_name.split(" ")[0]}` : ""}`}
      subtitle={isReset
        ? "Your administrator reset your two-factor authentication. Enter your current password to continue."
        : `Choose a password for signing in to ${APP_TITLE} with your email.`}
    >
      <form className="auth-form" onSubmit={submitCredentials}>
        <AuthField label="Email" hint="This is the address your invitation was sent to.">
          <input className="auth-input" type="email" autoComplete="username" value={info.email} readOnly />
        </AuthField>
        <AuthField label={isReset ? "Current password" : "Password"}>
          <input className="auth-input" type="password" autoComplete={isReset ? "current-password" : "new-password"} autoFocus required
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </AuthField>
        {!isReset && (
          <>
            <AuthField label="Confirm password">
              <input className="auth-input" type="password" autoComplete="new-password" required
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </AuthField>
            <PasswordChecklist password={password} confirm={confirm} />
          </>
        )}
        <AuthError message={error} />
        <button className="auth-btn auth-btn--primary" type="submit" disabled={busy || !canSubmitCredentials}>
          {busy ? "Saving…" : "Continue"}
        </button>
      </form>
    </AuthLayout>
  );
}
